<template>
  <canvas ref="canvas" class="shader-background"></canvas>
</template>

<script>
class PlingStarMap {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.ctx = this.canvas.getContext('2d');

    this.centerLabel  = opts.centerLabel  || 'PLING';
    this.centerColor  = opts.centerColor  || [0, 255, 200];
    this.starCount    = opts.starCount    || 350;
    this.dustCount    = opts.dustCount    || 90;
    this.minOrbit     = opts.minOrbit     || 0.15;
    this.maxOrbit     = opts.maxOrbit     || 0.65;
    this.impulseRate  = opts.impulseRate  != null ? opts.impulseRate  : 0.025;
    this.impulseSpeed = opts.impulseSpeed != null ? opts.impulseSpeed : 0.0015;

    this.planets  = [];
    this.impulses = [];
    this.time     = 0;
    this.raf      = null;
    this.W = 0; this.H = 0; this.cx = 0; this.cy = 0;
    this.mouseX = -9999; this.mouseY = -9999;

    this._anim = { mode: 'idle', progress: 1, duration: 3.5, elapsed: 0, onComplete: null };
    this._expand = 1;
    this._flash = 0;
    this._zoom = 1;

    this._palette = [
      [0,170,255],[255,106,0],[155,89,182],[46,204,113],
      [231,76,60],[243,156,18],[26,188,156],[255,0,102],
      [52,152,219],[241,196,15],[142,68,173],[230,126,34],
    ];
    this._colorIdx = 0;

    this._initStars();
    this._initDust();
    this._bindEvents();
    this._resize();

    if (opts.planets) this.setPlanets(opts.planets);

    this._loop = this._loop.bind(this);
    this._loop();
  }

  setPlanets(arr) {
    this.planets = arr.map((p, i, a) => this._normalize(p, i, a.length));
    this.impulses = [];
  }

  playIntro(duration = 3.5) {
    this._expand = 0;
    this._flash = 0;
    this._zoom = 1;
    this.impulses = [];
    this._anim = { mode: 'intro', progress: 0, duration, elapsed: 0, onComplete: null };
  }

  playOutro(duration = 2.5) {
    if (this._anim.mode !== 'idle') return;
    this._anim = { mode: 'outro', progress: 0, duration, elapsed: 0, onComplete: null };
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('mousemove', this._onMouse);
    window.removeEventListener('mouseleave', this._onLeave);
  }

  _normalize(p, index, total) {
    const label = p.label || `Node ${index}`;
    const color = p.color || this._palette[this._colorIdx++ % this._palette.length];
    const orbit = p.orbit != null ? p.orbit : (total > 1 ? (index + 1) / (total + 1) : 0.5);
    const speed = p.speed != null ? p.speed : 0.2 - orbit * 0.16 + 0.04;
    const tilt  = p.tilt  != null ? p.tilt  : 0.35 + Math.random() * 0.3;
    const rotation = p.rotation != null ? p.rotation
      : (index / Math.max(total, 1)) * Math.PI - Math.PI / 2 + (Math.random() - 0.5) * 0.4;
    const phase = p.phase != null ? p.phase : Math.random() * Math.PI * 2;
    const impulseRate = p.impulseRate != null ? p.impulseRate : null;
    const impulseSpeed = p.impulseSpeed != null ? p.impulseSpeed : null;

    const satellites = (p.satellites || []).map((s, si, sa) => ({
      label: s.label || `Moon ${si}`,
      color: s.color || color.map(c => Math.min(255, c + 40)),
      dist:  s.dist  != null ? s.dist  : 18 + si * 10,
      speed: s.speed != null ? s.speed : 0.3 + Math.random() * 0.3,
      phase: s.phase != null ? s.phase : (si / sa.length) * Math.PI * 2,
      tilt:  s.tilt  != null ? s.tilt  : 0.5 + Math.random() * 0.4,
      rotation: s.rotation != null ? s.rotation : (Math.random() - 0.5) * 1.5,
    }));

    return { label, color, orbit, speed, tilt, rotation, phase, satellites, impulseRate, impulseSpeed };
  }

  _initStars() {
    this.stars = Array.from({ length: this.starCount }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.2 + 0.3,
      sp: Math.random() * 2 + 0.5,
      off: Math.random() * Math.PI * 2,
      depth: Math.random(),
      dx: (Math.random() - 0.5) * 0.003,
      dy: (Math.random() - 0.5) * 0.001 + 0.001,
    }));
    this.shootingStars = [];
  }

  _initDust() {
    this.dust = Array.from({ length: this.dustCount }, () => ({
      x: Math.random() * 2 - 1, y: Math.random() * 2 - 1,
      vx: (Math.random() - 0.5) * 0.06, vy: (Math.random() - 0.5) * 0.06,
      r: Math.random() * 1.4 + 0.4, a: Math.random() * 0.2 + 0.03,
    }));
  }

  _bindEvents() {
    this._onResize = () => this._resize();
    this._onMouse  = e => { this.mouseX = e.clientX; this.mouseY = e.clientY; };
    this._onLeave  = () => { this.mouseX = -9999; this.mouseY = -9999; };
    window.addEventListener('resize', this._onResize);
    window.addEventListener('mousemove', this._onMouse);
    window.addEventListener('mouseleave', this._onLeave);
  }

  _resize() {
    const dpr = Math.min(window.devicePixelRatio, 2);
    this.W = window.innerWidth;
    this.H = window.innerHeight;
    this.canvas.width = this.W * dpr;
    this.canvas.height = this.H * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cx = this.W / 2;
    this.cy = this.H / 2;
  }

  _easeOutExpo(t) { return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t); }
  _easeInExpo(t) { return t <= 0 ? 0 : Math.pow(2, 10 * (t - 1)); }
  _easeInQuart(t) { return t * t * t * t; }

  _rgba(c, a) { return `rgba(${c[0]},${c[1]},${c[2]},${a})`; }

  _orbitR(normalized) {
    const base = Math.min(this.W, this.H);
    return base * (this.minOrbit + normalized * (this.maxOrbit - this.minOrbit));
  }

  _nodePos(idx) {
    const n = this.planets[idx];
    if (!n) return { x: this.cx, y: this.cy };
    const r = this._orbitR(n.orbit) * this._expand;
    const angle = n.phase + this.time * n.speed;
    const ex = Math.cos(angle) * r;
    const ey = Math.sin(angle) * r * n.tilt;
    const rot = n.rotation;
    return {
      x: this.cx + ex * Math.cos(rot) - ey * Math.sin(rot),
      y: this.cy + ex * Math.sin(rot) + ey * Math.cos(rot),
    };
  }

  _bezier(p0, cp, p1, t) {
    const u = 1 - t;
    return { x: u*u*p0.x + 2*u*t*cp.x + t*t*p1.x, y: u*u*p0.y + 2*u*t*cp.y + t*t*p1.y };
  }

  _spawnImpulse(idx) {
    const n = this.planets[idx];
    if (!n) return;
    const outward = Math.random() > 0.4;
    const baseSpeed = n.impulseSpeed != null ? n.impulseSpeed : this.impulseSpeed;
    this.impulses.push({
      idx, progress: outward ? 0 : 1,
      dir: outward ? 1 : -1,
      speed: baseSpeed + Math.random() * baseSpeed * 2,
      color: n.color,
      size: Math.random() * 1.0 + 0.8,
      trail: [],
    });
  }

  _tickAnim(dt) {
    const a = this._anim;
    if (a.mode === 'idle') return;

    a.elapsed += dt;
    a.progress = Math.min(1, a.elapsed / a.duration);

    if (a.mode === 'intro') {
      if (a.progress < 0.15) {
        this._flash = Math.sin(a.progress / 0.15 * Math.PI) * 0.9;
      } else {
        this._flash = Math.max(0, this._flash - dt * 2);
      }
      const expandT = Math.max(0, (a.progress - 0.05) / 0.95);
      this._expand = this._easeOutExpo(expandT);
      this._zoom = 1;
    }

    if (a.mode === 'outro') {
      this._expand = 1 - this._easeInQuart(a.progress);
      this._zoom = 1 + this._easeInExpo(a.progress) * 20;
      if (a.progress > 0.8) {
        this._flash = this._easeInExpo((a.progress - 0.8) / 0.2);
      }
    }

    if (a.progress >= 1) {
      if (a.mode === 'intro') {
        this._expand = 1;
        this._flash = 0;
        this._zoom = 1;
      }
      if (a.mode === 'outro') {
        this._expand = 0;
        this._flash = 1;
        this._zoom = 21;
      }
      a.mode = 'idle';
      if (a.onComplete) a.onComplete();
    }
  }

  _loop() {
    this.raf = requestAnimationFrame(this._loop);
    const dt = 0.016;
    this.time += dt;
    this._tickAnim(dt);

    const { ctx, W, H, cx, cy, time } = this;
    const rgba = (c, a) => this._rgba(c, a);
    const ACC = this.centerColor;
    const expand = this._expand;
    const zoom = this._zoom;

    ctx.clearRect(0, 0, W, H);

    // Apply zoom transform for outro
    if (zoom !== 1) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(zoom, zoom);
      ctx.translate(-cx, -cy);
    }

    // BG
    const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.7);
    bg.addColorStop(0, '#060f17');
    bg.addColorStop(0.5, '#040c12');
    bg.addColorStop(1, '#020608');
    ctx.fillStyle = bg;
    ctx.fillRect(cx - W, cy - H, W * 3, H * 3);

    // Stars
    for (const s of this.stars) {
      s.x += s.dx * dt; s.y += s.dy * dt;
      if (s.x < 0) s.x += 1; if (s.x > 1) s.x -= 1;
      if (s.y < 0) s.y += 1; if (s.y > 1) s.y -= 1;

      const parallax = 0.3 + s.depth * 0.7;
      const starExpand = 0.05 + expand * 0.95;
      const baseX = 0.5 + (s.x - 0.5) * starExpand;
      const baseY = 0.5 + (s.y - 0.5) * starExpand;
      const px = baseX * W + (this.mouseX > 0 ? (this.mouseX - cx) * 0.008 * parallax : 0);
      const py = baseY * H + (this.mouseY > 0 ? (this.mouseY - cy) * 0.008 * parallax : 0);

      const a = (0.1 + 0.4 * s.depth) * (0.5 + 0.5 * Math.sin(time * s.sp + s.off)) * Math.min(1, expand * 3);
      ctx.beginPath();
      ctx.arc(px, py, s.r * (0.6 + 0.4 * s.depth), 0, Math.PI * 2);
      ctx.fillStyle = rgba([180, 220, 255], a);
      ctx.fill();
    }

    // Shooting stars
    if (this._anim.mode === 'idle' && Math.random() < 0.004) {
      const angle = Math.PI * 0.15 + Math.random() * Math.PI * 0.3;
      const spd = 4 + Math.random() * 6;
      this.shootingStars.push({
        x: Math.random() * W * 0.8, y: Math.random() * H * 0.3,
        vx: Math.cos(angle) * spd, vy: Math.sin(angle) * spd,
        life: 1.0, decay: 0.01 + Math.random() * 0.015, len: 40 + Math.random() * 60,
      });
    }
    for (let i = this.shootingStars.length - 1; i >= 0; i--) {
      const ss = this.shootingStars[i];
      ss.x += ss.vx; ss.y += ss.vy; ss.life -= ss.decay;
      if (ss.life <= 0) { this.shootingStars.splice(i, 1); continue; }
      const norm = Math.hypot(ss.vx, ss.vy);
      const tailX = ss.x - (ss.vx / norm) * ss.len * ss.life;
      const tailY = ss.y - (ss.vy / norm) * ss.len * ss.life;
      const grad = ctx.createLinearGradient(tailX, tailY, ss.x, ss.y);
      grad.addColorStop(0, rgba([180, 220, 255], 0));
      grad.addColorStop(1, rgba([220, 240, 255], ss.life * 0.6));
      ctx.beginPath(); ctx.moveTo(tailX, tailY); ctx.lineTo(ss.x, ss.y);
      ctx.strokeStyle = grad; ctx.lineWidth = 1.2; ctx.stroke();
      ctx.beginPath(); ctx.arc(ss.x, ss.y, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = rgba([220, 240, 255], ss.life * 0.8); ctx.fill();
    }

    // Dust
    for (const d of this.dust) {
      d.x += d.vx * dt; d.y += d.vy * dt;
      if (d.x < -1.2) d.x = 1.2; if (d.x > 1.2) d.x = -1.2;
      if (d.y < -1.2) d.y = 1.2; if (d.y > 1.2) d.y = -1.2;
      const dx = d.x * expand, dy = d.y * expand;
      ctx.beginPath();
      ctx.arc(cx + dx * cx, cy + dy * cy, d.r, 0, Math.PI * 2);
      ctx.fillStyle = rgba(ACC, d.a * (0.5 + 0.5 * Math.sin(time + d.x * 5)) * expand);
      ctx.fill();
    }

    const center = { x: cx, y: cy };
    const count = this.planets.length;
    const positions = this.planets.map((_, i) => this._nodePos(i));

    // Orbit arcs
    if (expand > 0.1) {
      const arcAlpha = Math.min(1, (expand - 0.1) / 0.4);
      const ARC_LENGTH = Math.PI * 0.8;
      const ARC_SEGMENTS = 40;
      for (let i = 0; i < count; i++) {
        const n = this.planets[i];
        const r = this._orbitR(n.orbit) * expand;
        const currentAngle = n.phase + time * n.speed;

        for (let s = 0; s < ARC_SEGMENTS; s++) {
          const t0 = s / ARC_SEGMENTS;
          const t1 = (s + 1) / ARC_SEGMENTS;
          const a0 = currentAngle - ARC_LENGTH * (1 - t0);
          const a1 = currentAngle - ARC_LENGTH * (1 - t1);
          const fade = t0 * arcAlpha;

          ctx.save();
          ctx.translate(cx, cy); ctx.rotate(n.rotation); ctx.scale(1, n.tilt);
          ctx.beginPath(); ctx.arc(0, 0, r, a0, a1);
          ctx.restore();
          ctx.strokeStyle = rgba(n.color, fade * 0.06);
          ctx.lineWidth = 3; ctx.stroke();

          ctx.save();
          ctx.translate(cx, cy); ctx.rotate(n.rotation); ctx.scale(1, n.tilt);
          ctx.beginPath(); ctx.arc(0, 0, r, a0, a1);
          ctx.restore();
          ctx.strokeStyle = rgba(n.color, fade * 0.14);
          ctx.lineWidth = 0.7; ctx.stroke();
        }
      }
    }

    // Connection curves
    if (expand > 0.3) {
      const connAlpha = Math.min(1, (expand - 0.3) / 0.3);
      for (let i = 0; i < count; i++) {
        const sp = positions[i]; const n = this.planets[i];
        const dx = sp.x - center.x, dy = sp.y - center.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 1) continue;
        const perpX = -dy / dist, perpY = dx / dist;
        const bulge = dist * 0.15 + Math.sin(time + i) * 8;
        const midX = (center.x + sp.x) / 2 + perpX * bulge;
        const midY = (center.y + sp.y) / 2 + perpY * bulge;

        ctx.beginPath(); ctx.moveTo(center.x, center.y);
        ctx.quadraticCurveTo(midX, midY, sp.x, sp.y);
        ctx.strokeStyle = rgba(n.color, (0.035 + 0.015 * Math.sin(time * 2 + i)) * connAlpha);
        ctx.lineWidth = 0.8; ctx.stroke();
      }
    }

    // Spawn impulses
    if (expand > 0.85) {
      for (let i = 0; i < count; i++) {
        const rate = this.planets[i].impulseRate != null
          ? this.planets[i].impulseRate : this.impulseRate;
        if (rate > 0 && Math.random() < rate / count) {
          this._spawnImpulse(i);
        }
      }
    }

    // Impulses
    for (let i = this.impulses.length - 1; i >= 0; i--) {
      const imp = this.impulses[i];
      if (imp.idx >= count) { this.impulses.splice(i, 1); continue; }

      imp.progress += imp.speed * imp.dir;
      const sp = positions[imp.idx];
      const dx = sp.x - center.x, dy = sp.y - center.y;
      const dist = Math.hypot(dx, dy);
      const perpX = dist > 0 ? -dy / dist : 0, perpY = dist > 0 ? dx / dist : 0;
      const cp = { x: (center.x + sp.x) / 2 + perpX * dist * 0.15,
                    y: (center.y + sp.y) / 2 + perpY * dist * 0.15 };

      const t = Math.max(0, Math.min(1, imp.progress));
      const pos = this._bezier(center, cp, sp, t);

      imp.trail.push({ x: pos.x, y: pos.y });
      if (imp.trail.length > 14) imp.trail.shift();

      for (let ti = 0; ti < imp.trail.length; ti++) {
        const tp = imp.trail[ti];
        ctx.beginPath();
        ctx.arc(tp.x, tp.y, imp.size * (ti / imp.trail.length) * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = rgba(imp.color, (ti / imp.trail.length) * 0.25);
        ctx.fill();
      }

      ctx.beginPath(); ctx.arc(pos.x, pos.y, imp.size, 0, Math.PI * 2);
      ctx.fillStyle = rgba(imp.color, 0.55); ctx.fill();

      const hg = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, imp.size * 4);
      hg.addColorStop(0, rgba(imp.color, 0.08)); hg.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(pos.x, pos.y, imp.size * 4, 0, Math.PI * 2);
      ctx.fillStyle = hg; ctx.fill();

      if (imp.progress > 1.05 || imp.progress < -0.05) this.impulses.splice(i, 1);
    }

    // Planets + moons
    if (expand > 0.15) {
      const nodeAlpha = Math.min(1, (expand - 0.15) / 0.3);

      for (let i = 0; i < count; i++) {
        const sp = positions[i]; const n = this.planets[i];
        const pulse = 1 + 0.25 * Math.sin(time * 2 + i * 1.3);

        // Moons
        if (n.satellites && n.satellites.length && expand > 0.5) {
          const moonAlpha = Math.min(1, (expand - 0.5) / 0.3);
          for (let mi = 0; mi < n.satellites.length; mi++) {
            const moon = n.satellites[mi];
            const mAngle = moon.phase + time * moon.speed;
            const mex = Math.cos(mAngle) * moon.dist * expand;
            const mey = Math.sin(mAngle) * moon.dist * moon.tilt * expand;
            const mrot = moon.rotation;
            const mx = sp.x + mex * Math.cos(mrot) - mey * Math.sin(mrot);
            const my = sp.y + mex * Math.sin(mrot) + mey * Math.cos(mrot);

            ctx.save();
            ctx.translate(sp.x, sp.y); ctx.rotate(mrot); ctx.scale(1, moon.tilt);
            ctx.beginPath(); ctx.arc(0, 0, moon.dist * expand, 0, Math.PI * 2);
            ctx.restore();
            ctx.strokeStyle = rgba(moon.color, 0.06 * moonAlpha); ctx.lineWidth = 0.4; ctx.stroke();

            ctx.beginPath(); ctx.moveTo(sp.x, sp.y); ctx.lineTo(mx, my);
            ctx.strokeStyle = rgba(moon.color, 0.05 * moonAlpha); ctx.lineWidth = 0.4; ctx.stroke();

            const mg = ctx.createRadialGradient(mx, my, 0, mx, my, 10);
            mg.addColorStop(0, rgba(moon.color, 0.1 * moonAlpha)); mg.addColorStop(1, 'transparent');
            ctx.beginPath(); ctx.arc(mx, my, 10, 0, Math.PI * 2);
            ctx.fillStyle = mg; ctx.fill();

            ctx.beginPath(); ctx.arc(mx, my, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = rgba(moon.color, 0.7 * moonAlpha); ctx.fill();

            ctx.font = '400 7px Rajdhani, sans-serif';
            ctx.fillStyle = rgba(moon.color, 0.15 * moonAlpha); ctx.textAlign = 'center';
            ctx.fillText(moon.label.toUpperCase(), mx, my + 11);
          }
        }

        // Planet glow
        const g = ctx.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, 22 * pulse);
        g.addColorStop(0, rgba(n.color, 0.1 * nodeAlpha)); g.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(sp.x, sp.y, 22 * pulse, 0, Math.PI * 2);
        ctx.fillStyle = g; ctx.fill();

        ctx.beginPath(); ctx.arc(sp.x, sp.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = rgba(n.color, 0.8 * nodeAlpha); ctx.fill();

        ctx.beginPath(); ctx.arc(sp.x, sp.y, 7 * pulse, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(n.color, 0.12 * nodeAlpha); ctx.lineWidth = 0.5; ctx.stroke();

        ctx.font = '500 9px Rajdhani, sans-serif';
        ctx.fillStyle = rgba(n.color, 0.2 * nodeAlpha); ctx.textAlign = 'center';
        ctx.fillText(n.label.toUpperCase(), sp.x, sp.y + 17);
      }
    }

    // Central node
    const cp2 = 1 + 0.15 * Math.sin(time * 1.5);
    const introBoost = this._anim.mode === 'intro' && expand < 0.3 ? (1 - expand / 0.3) * 3 : 0;

    const coreSize = (9 + introBoost * 8);
    const innerR = (35 + introBoost * 40) * cp2;
    const outerR = (120 + introBoost * 80) * cp2;

    const cg1 = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR);
    cg1.addColorStop(0, rgba(ACC, 0.06 + introBoost * 0.08));
    cg1.addColorStop(0.4, rgba([0, 130, 200], 0.015 + introBoost * 0.03));
    cg1.addColorStop(1, 'transparent');
    ctx.beginPath(); ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = cg1; ctx.fill();

    const cg2 = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR);
    cg2.addColorStop(0, rgba(ACC, 0.2 + introBoost * 0.3));
    cg2.addColorStop(0.6, rgba(ACC, 0.06 + introBoost * 0.1));
    cg2.addColorStop(1, 'transparent');
    ctx.beginPath(); ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fillStyle = cg2; ctx.fill();

    ctx.beginPath(); ctx.arc(cx, cy, coreSize, 0, Math.PI * 2);
    ctx.fillStyle = rgba(ACC, 0.9); ctx.fill();

    for (const r of [18, 28]) {
      ctx.beginPath(); ctx.arc(cx, cy, (r + introBoost * 15) * cp2, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(ACC, 0.07 + introBoost * 0.05); ctx.lineWidth = 0.5; ctx.stroke();
    }

    if (expand > 0.4) {
      const labelAlpha = Math.min(1, (expand - 0.4) / 0.3);
      ctx.font = '700 11px Orbitron, monospace';
      ctx.fillStyle = rgba(ACC, 0.3 * labelAlpha); ctx.textAlign = 'center';
      ctx.fillText(this.centerLabel, cx, cy + 42);
    }

    // Restore zoom transform
    if (zoom !== 1) {
      ctx.restore();
    }

    // Flash overlay — drawn outside zoom for clean fullscreen
    if (this._flash > 0.01) {
      ctx.fillStyle = rgba(ACC, this._flash * 0.7);
      ctx.fillRect(0, 0, W, H);
      const fg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.4);
      fg.addColorStop(0, rgba([255, 255, 255], this._flash * 0.5));
      fg.addColorStop(1, 'transparent');
      ctx.fillStyle = fg;
      ctx.fillRect(0, 0, W, H);
    }

    // Mouse glow
    if (this._anim.mode === 'idle' && this.mouseX > 0) {
      const md = Math.hypot(this.mouseX - cx, this.mouseY - cy);
      if (md < 400) {
        const ma = (1 - md / 400) * 0.025;
        const mg2 = ctx.createRadialGradient(this.mouseX, this.mouseY, 0, this.mouseX, this.mouseY, 100);
        mg2.addColorStop(0, rgba(ACC, ma)); mg2.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(this.mouseX, this.mouseY, 100, 0, Math.PI * 2);
        ctx.fillStyle = mg2; ctx.fill();
      }
    }
  }
}

export default {
  name: 'LoginShaderBackground',
  data() {
    return {
      map: null,
    }
  },
  mounted() {
    this.map = new PlingStarMap(this.$refs.canvas, {
      centerLabel: 'PLING',
      centerColor: [0, 255, 200],
      planets: [
        { label: 'Orders Pro', color: [0, 170, 255], orbit: 0.25, speed: 0.10, rotation: 0.4, impulseRate: 0.05,
          satellites: [
            { label: 'Sendungsverfolgung' },
            { label: 'E-Rechnungen' },
            { label: 'Retourenportal' },
            { label: 'Kassensystem Pro' },
            { label: 'Coupons & Vouchers' },
          ]},
        { label: 'PIM Pro', color: [46, 204, 113], orbit: 0.50, speed: 0.06, rotation: -0.5,
          satellites: [
            { label: 'Spec Sheet' },
            { label: 'Marketing Pro' },
          ]},
        { label: 'Multichannel', color: [155, 89, 182], orbit: 0.75, speed: 0.04, rotation: 0.9, impulseRate: 0.04,
          satellites: [
            { label: 'Marktplätze' },
            { label: 'Connector' },
          ]},
        { label: 'Trade Pro', color: [255, 106, 0], orbit: 0.40, speed: 0.08, rotation: -1.1,
          satellites: [
            { label: 'Kundengruppen' },
            { label: 'Preislisten' },
            { label: 'Smart Search' },
          ]},
        { label: 'CRM', color: [231, 76, 60], orbit: 0.60, speed: 0.05, rotation: 0.2,
          satellites: [
            { label: 'Kontakte' },
            { label: 'Pipeline' },
          ]},
        { label: 'Publishing', color: [26, 188, 156], orbit: 0.85, speed: 0.03, rotation: -0.3,
          satellites: [
            { label: 'Publish' },
            { label: 'Mehrsprachlichkeit' },
          ]},
        { label: 'AI Pro', color: [243, 156, 18], orbit: 0.15, speed: 0.14, rotation: 1.4,
          satellites: [
            { label: 'ChatBot Pro' },
            { label: 'Translation' },
          ]},
        { label: 'Personal', color: [52, 152, 219], orbit: 0.92, speed: 0.025, rotation: -0.8,
          satellites: [
            { label: 'Time Tracking' },
            { label: 'Portal' },
          ]},
      ],
    });

    this.map.playIntro(3.5);
  },
  methods: {
    playOutro() {
      if (this.map) this.map.playOutro(2.5);
    },
    playIntro() {
      if (this.map) this.map.playIntro(3.5);
    },
  },
  beforeDestroy() {
    if (this.map) this.map.destroy();
  },
}
</script>

<style lang="scss" scoped>
.shader-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}
</style>
